import { Button } from "@majitap/ui";
import { type DropdownItem, DropdownMenu } from "@majitap/ui/arc/dropdown-menu/dropdown-menu";
import { useState } from "react";
import type { DeliveryRecord } from "../../deliveries";
import { actionsFor, dispatchActions, nextStage, STAGE_ACTION } from "../../dispatch";
import {
  AssignDrawer,
  CompleteDialog,
  OfferDialog,
  ProgressDialog,
  ReasonDialog,
  ResetDialog,
  ResumeDialog,
  useRefreshBoard,
} from "../dispatch/actions";
import { RerouteDialog } from "../dispatch/reroute";
import { LogIncidentDialog } from "../incidents/dialogs";
import { ConfirmDialog } from "../shared/confirm";

type Action =
  | "assign"
  | "unassign"
  | "suspend"
  | "resume"
  | "reroute"
  | "offer"
  | "takeBack"
  | "progress"
  | "complete"
  | "incident"
  | "reset";

/**
 * What staff can do with the delivery (`Console / Delivery`, header and More): the next step as a
 * button, the rest in a menu. Each opens Dispatch's own dialog, so the rules are the board's.
 */
export function DeliveryActions({ delivery: d }: { delivery: DeliveryRecord }) {
  const refresh = useRefreshBoard();
  const [open, setOpen] = useState<Action | null>(null);
  const close = () => setOpen(null);
  const can = actionsFor(d.status, d.dispatch_mode);
  const planned = Boolean(d.assignment?.driver);
  const stage = planned ? nextStage(d.status, Boolean(d.assignment?.fillsite)) : null;

  // the step the delivery waits on, first; everything else goes in More
  const items: (DropdownItem & { action: Action })[] = [];
  const add = (action: Action, label: string, more: Partial<DropdownItem> = {}) =>
    items.push({ action, label, onSelect: () => setOpen(action), ...more });
  if (stage) add("progress", STAGE_ACTION[stage]);
  if (can.complete && planned) add("complete", "Mark delivered");
  if (can.assign) add("assign", planned ? "Reassign" : "Assign a driver");
  if (can.unassign) add("unassign", "Unassign");
  if (can.offer) add("offer", "Offer to partners");
  if (can.takeBack) add("takeBack", "Take back from partners");
  if (can.suspend) add("suspend", "Suspend", { destructive: true });
  if (can.resume) add("resume", "Resume");
  if (can.reroute) add("reroute", "Reroute");
  if (can.incident) add("incident", "Log an incident", { separatorBefore: items.length > 1 });
  if (can.reset) add("reset", "Reset to pending", { destructive: true, separatorBefore: items.length > 1 });
  const [primary, ...more] = items;

  return (
    <>
      {more.length > 0 ? <DropdownMenu label="More" items={more} /> : null}
      {primary ? (
        // logging an incident is never the step the delivery waits on
        <Button
          variant={primary.action === "incident" ? "secondary" : "primary"}
          onClick={() => setOpen(primary.action)}
        >
          {primary.label}
        </Button>
      ) : null}

      {open === "assign" ? <AssignDrawer delivery={d} onClose={close} /> : null}
      {open === "unassign" ? (
        <ReasonDialog
          title={`Unassign ${d.delivery_number}?`}
          description="The truck and driver come off it. A water load keeps its fill site."
          action="Unassign"
          run={(reason) => dispatchActions.unassign(d.id, reason)}
          onClose={close}
        />
      ) : null}
      {open === "suspend" ? (
        <ReasonDialog
          title={`Suspend ${d.delivery_number}?`}
          description="It waits at the customer until you resume or reroute it."
          action="Suspend"
          danger
          scope="delivery_suspended"
          run={(reason, reasonId) => dispatchActions.suspend(d.id, reason, reasonId)}
          onClose={close}
        />
      ) : null}
      {open === "resume" ? <ResumeDialog delivery={d} onClose={close} /> : null}
      {open === "takeBack" ? (
        <ConfirmDialog
          title={`Take ${d.delivery_number} back from partners?`}
          description="It leaves the partner job board, and you plan it here. A partner can't claim it any more."
          action="Take it back"
          run={() => dispatchActions.takeBack(d.id)}
          onDone={() => refresh()}
          onClose={close}
        />
      ) : null}
      {open === "reroute" ? <RerouteDialog delivery={d} onClose={close} /> : null}
      {open === "offer" ? <OfferDialog delivery={d} onClose={close} /> : null}
      {open === "progress" && stage ? <ProgressDialog delivery={d} to={stage} onClose={close} /> : null}
      {open === "complete" ? <CompleteDialog delivery={d} onClose={close} /> : null}
      {open === "incident" ? <LogIncidentDialog delivery={d} onClose={close} /> : null}
      {open === "reset" ? <ResetDialog delivery={d} onClose={close} /> : null}
    </>
  );
}
